import Cookies from 'js-cookie';

import { ITokenKeyEnum } from '~/enums/tokenKey.enum';

export type ITokens = {
  refreshToken: string;
  accessToken: string;
  tokenType: string;
  expiresIn: string;
  scope: string;
};

export type ISetAllTokensInput = {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  scope?: string;
  tokenType?: string;
};

// vol_* cookie names shared across all portals under .veilingonline.com.br
const VOL_KEYS: Record<string, string> = {
  [ITokenKeyEnum.ACCESS_TOKEN]: 'vol_at',
  [ITokenKeyEnum.REFRESH_TOKEN]: 'vol_rt',
  [ITokenKeyEnum.TOKEN_TYPE]: 'vol_tt',
  [ITokenKeyEnum.EXPIRES_IN]: 'vol_exp',
  [ITokenKeyEnum.SCOPE]: 'vol_sc',
};

const volKey = (key: string): string => VOL_KEYS[key] ?? key;

const isParentDomain =
  typeof location !== 'undefined' && location.hostname.includes('veilingonline.com.br');

const isHttps = typeof location !== 'undefined' && location.protocol === 'https:';

const baseCookieOptions: Cookies.CookieAttributes = {
  domain: isParentDomain ? '.veilingonline.com.br' : undefined,
  path: '/',
  secure: isHttps,
  sameSite: 'lax' as const,
  expires: 1,
};

const rtCookieOptions: Cookies.CookieAttributes = {
  ...baseCookieOptions,
  expires: 30,
};

const ALL_KEYS = [
  ITokenKeyEnum.ACCESS_TOKEN,
  ITokenKeyEnum.REFRESH_TOKEN,
  ITokenKeyEnum.TOKEN_TYPE,
  ITokenKeyEnum.EXPIRES_IN,
  ITokenKeyEnum.SCOPE,
] as const;

// Guards (evita erro em SSR/test)
const canUseCookies = (): boolean => typeof document !== 'undefined';
const canUseLocalStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export type IStorageLike = {
  get: (key: string) => string;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
};

const cookieStorage: IStorageLike = {
  get: (key: string) => {
    if (!canUseCookies()) return '';
    return Cookies.get(volKey(key)) ?? '';
  },
  set: (key: string, value: string) => {
    if (!canUseCookies()) return;
    Cookies.set(volKey(key), value, baseCookieOptions);
  },
  remove: (key: string) => {
    if (!canUseCookies()) return;
    Cookies.remove(volKey(key), { path: baseCookieOptions.path, domain: baseCookieOptions.domain });
  },
};

const localStorageAdapter: IStorageLike = {
  get: (key: string) => {
    if (!canUseLocalStorage()) return '';
    try {
      return window.localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  },
  set: (key: string, value: string) => {
    if (!canUseLocalStorage()) return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // noop (quota/private mode)
    }
  },
  remove: (key: string) => {
    if (!canUseLocalStorage()) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // noop
    }
  },
};

const getPrimary = (key: string): string => cookieStorage.get(key);

const normalizeTokenType = (value?: string): string =>
  (value ?? '').trim() || ITokenKeyEnum.DEFAULT_TOKEN_TYPE;

const tokenChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('vol-sso-tokens') : null;

if (tokenChannel) {
  tokenChannel.onmessage = (e: MessageEvent) => {
    if (e.data?.type === 'logout') {
      ALL_KEYS.forEach(k => cookieStorage.remove(k));
    }
  };
}

export const useTokenStore = {
  // ---------
  // Getters (cookie -> fallback ls)
  // ---------
  getAccess: (): string => getPrimary(ITokenKeyEnum.ACCESS_TOKEN),

  getRefresh: (): string => getPrimary(ITokenKeyEnum.REFRESH_TOKEN),

  getScope: (): string => getPrimary(ITokenKeyEnum.SCOPE),

  getTokenType: (): string => normalizeTokenType(getPrimary(ITokenKeyEnum.TOKEN_TYPE)),

  getExpiresIn: (): string => getPrimary(ITokenKeyEnum.EXPIRES_IN),

  getAuthorizationHeader: (): string => {
    const accessToken = useTokenStore.getAccess();
    if (!accessToken) return '';
    return `${useTokenStore.getTokenType()} ${accessToken}`;
  },

  getTokens: (): ITokens => ({
    accessToken: useTokenStore.getAccess(),
    refreshToken: useTokenStore.getRefresh(),
    tokenType: useTokenStore.getTokenType(),
    expiresIn: useTokenStore.getExpiresIn(),
    scope: useTokenStore.getScope(),
  }),

  // ---------
  // Actions (persistência)
  // ---------
  setCookies: (data: ISetAllTokensInput): void => {
    cookieStorage.set(ITokenKeyEnum.ACCESS_TOKEN, data.accessToken);
    Cookies.set(volKey(ITokenKeyEnum.REFRESH_TOKEN), data.refreshToken, rtCookieOptions);
    cookieStorage.set(ITokenKeyEnum.EXPIRES_IN, data.expiresIn);
    cookieStorage.set(
      ITokenKeyEnum.TOKEN_TYPE,
      normalizeTokenType(data.tokenType ?? ITokenKeyEnum.DEFAULT_TOKEN_TYPE),
    );
    if (data.scope) cookieStorage.set(ITokenKeyEnum.SCOPE, data.scope);
    else cookieStorage.remove(ITokenKeyEnum.SCOPE);
  },

  setLocalStorage: (data: ISetAllTokensInput): void => {
    localStorageAdapter.set(ITokenKeyEnum.ACCESS_TOKEN, data.accessToken);
    localStorageAdapter.set(ITokenKeyEnum.REFRESH_TOKEN, data.refreshToken);
    localStorageAdapter.set(ITokenKeyEnum.EXPIRES_IN, data.expiresIn);
    localStorageAdapter.set(
      ITokenKeyEnum.TOKEN_TYPE,
      normalizeTokenType(data.tokenType ?? ITokenKeyEnum.DEFAULT_TOKEN_TYPE),
    );
    if (data.scope) localStorageAdapter.set(ITokenKeyEnum.SCOPE, data.scope);
    else localStorageAdapter.remove(ITokenKeyEnum.SCOPE);
  },

  setAll: (data: ISetAllTokensInput): void => {
    useTokenStore.setCookies(data);
  },

  clearAll: (): void => {
    ALL_KEYS.forEach(k => cookieStorage.remove(k));
  },

  buildTokenQueryParams: (options?: {
    requireTokens?: boolean;
    withQuestionMark?: boolean;
  }): string => {
    const requireTokens = options?.requireTokens ?? true;
    const withQuestionMark = options?.withQuestionMark ?? true;

    const accessToken = useTokenStore.getAccess();
    const refreshToken = useTokenStore.getRefresh();

    if (requireTokens && (!accessToken || !refreshToken)) return '';

    const params = new URLSearchParams();
    params.set('accessToken', accessToken);
    params.set('refreshToken', refreshToken);
    params.set('tokenType', useTokenStore.getTokenType());
    params.set('expiresIn', useTokenStore.getExpiresIn());
    params.set('scope', useTokenStore.getScope());

    const qs = params.toString();
    if (!qs) return '';

    return withQuestionMark ? `?${qs}` : qs;
  },
};
